// =============================================================================
// printDocumentTitle — the house file-naming convention for every print/
// Save-as-PDF popup this app opens (Jason's standing convention: the browser
// save-as-PDF dialog defaults its filename to document.title, so the popup's
// <title> IS the saved filename in practice).
//
// Convention (exact segment order): {LastName} — {RoleTitle} — {Artifact} —
// {Company} — {YYYY-MM-DD}. e.g. "ORourke — Solutions Architect — Resume —
// Anthropic — 2026-07-05". A DRIVE-BUILD lane implements the SAME convention
// server-side; this module is the FE's own self-contained implementation —
// no cross-imports from files outside this lane's scope, per its own brief.
//
// NO INVENTION: every segment is OMITTED (never a placeholder like "Unknown
// Company") when the caller doesn't have the real, served value for it. Only
// `lastNameOrNull` does any derivation (splitting a real displayName), and it
// returns null rather than guessing when there's nothing genuine to split —
// same discipline lib/identity.ts's firstNameOrNull already establishes for
// the first-name half of this exact field.
// =============================================================================

/**
 * The user's last name, derived ONLY from their real displayName — the LAST
 * whitespace-delimited token, exactly as stored (no re-casing). Returns null
 * — never a fabricated fallback — when displayName is null/undefined/blank,
 * or when it has only a single token (no real last name to split out; a bare
 * first name should not be echoed back as if it were a surname). Mirrors
 * identity.ts's firstNameOrNull sibling-for-sibling.
 */
export function lastNameOrNull(displayName: string | null | undefined): string | null {
  if (!displayName) return null;
  const trimmed = displayName.trim();
  if (trimmed.length === 0) return null;
  const tokens = trimmed.split(/\s+/);
  if (tokens.length < 2) return null;
  const last = tokens[tokens.length - 1];
  return last.length > 0 ? last : null;
}

export type PrintArtifact = 'Resume' | 'Cover Letter' | 'Alignment Essay';

export interface PrintDocumentTitleInput {
  /** The signed-in user's real, served displayName — null/undefined when the
   *  screen genuinely has none in scope (never invented). */
  displayName?: string | null;
  artifact: PrintArtifact;
  /** The served job title for this document, if this print is job-specific. */
  roleTitle?: string | null;
  /** The served company name for this document, if this print is job-specific. */
  company?: string | null;
  /** Injectable for tests; defaults to the real wall-clock date (browser-
   *  local — there is no server round-trip for "what day is it" on a
   *  client-only print action, so this follows this file's own existing
   *  precedent (api.ts's JSON-export filename) rather than routing through
   *  the backend's TemporalService, which governs server-side generation. */
  now?: Date;
}

/**
 * Build the house print-document title/filename. Every segment is present
 * only when the caller supplied a real, non-blank value for it — a missing
 * name, role, or company silently DROPS that segment (never a placeholder),
 * so "Resume — 2026-07-05" is a valid, honest output when nothing else is
 * known. `artifact` and the date are the only two segments that always
 * render (the artifact type and the date are never "unknown" — they're
 * either passed by the caller or read from the real clock).
 */
export function buildPrintDocumentTitle(input: PrintDocumentTitleInput): string {
  const last = lastNameOrNull(input.displayName);
  const role = input.roleTitle?.trim() || null;
  const company = input.company?.trim() || null;
  const date = (input.now ?? new Date()).toISOString().slice(0, 10);

  const segments = [last, role, input.artifact, company, date].filter(
    (s): s is string => s !== null && s.length > 0,
  );
  return segments.join(' — ');
}
